import { Component } from 'preact';
import { TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD } from '../lib/medievalTheme';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import { keystoneCostForMode } from '../utils/arena/draftApi';
import { matchModeLabel } from '../utils/arena/gameModes';
import { BOOSTER_SETS } from '../utils/arena/packsApi';
import { getLocalApiOrigin } from '../utils/localApi';
import AcceptPayControl from './ui/AcceptPayControl';
import { UI } from '../utils/arena/uiSounds';
import { cn } from '../lib/utils';
import { t, k } from '../localization/t';

// The draft / sealed invite in a DM, on the same plate as ReplayShareCard.
//
// This IS the chat bubble, not something drawn inside one. renderMessage
// hands draft-invite messages straight here without its usual wrapper, the
// same way it does replays. The old shape put a gold-bordered box inside a
// bubble, so one invite drew three nested rounded rectangles and read as a
// receipt rather than as an event you are being asked to join.
//
// Everything on the card comes from the metadata the sender snapshotted at
// invite time (`app.jsx`, sendChatMessage type: 'draft-invite'): the set, the
// mode, the pod size and the entry cost. Nothing is re-derived, because the
// recipient cannot read the event row until they have accepted.

const LABEL_SHADOW = '0 1px 2px rgba(0,0,0,0.95), 0 0 6px rgba(0,0,0,0.85), 0 0 14px rgba(0,0,0,0.6)';

// The pack stands in the right third of the plate, and everything the card
// writes stays inside this gutter, so no label ever crosses the artwork.
// The two numbers move together: widen the pack and the text column has to
// give up the same width, or the two meet in the middle.
const PACK_GUTTER = '38%';
const PACK_TILT = '-3deg';
// The packshot's box. Fixed on both axes with object-fit: contain, so every
// set's art is letterboxed into the same rectangle and lands in the same
// place whatever its native canvas is.
const PACK_BOX = { right: '2%', top: '-2%', width: '34%', height: '104%' };

// The three packshots do not fill their canvases equally: Beta's art runs
// edge to edge, Gothic leaves a thin margin, and Arthurian floats in a
// square 1024 canvas at about three quarters height. Contained in one box
// they render at three different sizes, which is what made the Arthurian
// invite look like a smaller product than the other two.
//
// Each factor brings this set's VISIBLE pack to the height Beta's renders
// at. Two things feed it: how much of its own canvas the art occupies, and
// which axis object-fit: contain binds on against PACK_BOX. Arthurian is
// the big one because its square canvas binds on width, so contain shrinks
// it long before its height fills the box. Re-derive after any packshot is
// re-exported or PACK_BOX changes:
//
//   wb, hb = box width, box height          (in the same unit)
//   iw, ih = canvas size;  ah = alpha bbox height
//   visible = min(wb / iw, hb / ih) * ah    ->  scale = visible(beta) / visible(set)
export const PACK_SCALE = { beta: 1, gothic: 1.05, arthurian: 2.28 };

// Per-set wash behind the pack, sampled from each set's wrapper art so the
// card carries the set's own colour before a single word is read. Beta is
// the cold coast, Arthurian the grey-green field, Gothic the wine crypt.
const SET_WASH = {
  beta: 'radial-gradient(ellipse 90% 120% at 78% 50%, rgba(86,132,150,0.55) 0%, rgba(30,48,58,0.5) 45%, rgba(8,10,12,0.7) 100%)',
  arthurian: 'radial-gradient(ellipse 90% 120% at 78% 50%, rgba(122,116,92,0.5) 0%, rgba(48,46,38,0.5) 45%, rgba(10,10,8,0.7) 100%)',
  gothic: 'radial-gradient(ellipse 90% 120% at 78% 50%, rgba(112,52,96,0.5) 0%, rgba(48,24,42,0.5) 45%, rgba(10,7,10,0.72) 100%)',
};
const FALLBACK_WASH = 'radial-gradient(ellipse 90% 120% at 78% 50%, rgba(120,92,44,0.45) 0%, rgba(42,32,16,0.5) 45%, rgba(10,8,5,0.72) 100%)';

// Draft and sealed are event modes and have no entry in the match-mode
// table, so they get named here; anything else an invite could carry
// (ranked, casual, sandbox) already has a label there.
const EVENT_MODE_WORD = { draft: k('Draft'), sealed: k('Sealed') };

function modeWord(mode) {
  if (EVENT_MODE_WORD[mode]) return t(EVENT_MODE_WORD[mode]);
  const label = matchModeLabel(mode);
  return label ? t(label) : t('Event');
}

function assetUrl(name) {
  return `${getLocalApiOrigin()}/game-assets/${name}`;
}

/** The set's wrapper art and packshot, or nulls for a set we ship no art for. */
function setArt(setKey) {
  if (!setKey || !BOOSTER_SETS[setKey]) return { panel: null, pack: null };
  return {
    panel: assetUrl(`booster-${setKey}-panel.webp`),
    pack: assetUrl(`booster-${setKey}.webp`),
  };
}

/** Sender's face and name, the chip that says who is asking. */
function Sender({ name, avatarId, sorceryCards }) {
  const url = avatarId ? resolveAvatarUrl({ profileAvatar: avatarId }, sorceryCards) : null;
  return (
    <span className="flex items-center gap-1 min-w-0">
      <span
        className="shrink-0 overflow-hidden rounded-full"
        style={{
          width: '1.125cqw',
          height: '1.125cqw',
          background: '#1a1611',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)',
        }}
      >
        {url ? (
          <AvatarImage src={url} className="w-full h-full" />
        ) : null}
      </span>
      <span className="truncate text-[0.6875cqw] font-semibold" style={{ color: TEXT_PRIMARY, textShadow: LABEL_SHADOW }}>
        {name}
      </span>
    </span>
  );
}

/**
 * What the card knows about the event, on one line, dot separated.
 *
 * The same shape the Notifications menu writes its invite rows in. These
 * facts used to sit in floating rounded chips, which gave three small
 * islands with nothing holding them to the card; one line hangs off the
 * title like the rest of the text column.
 *
 * Deliberately carries no price. The entry cannot be paid without picking a
 * currency on the Accept control, so the control is the one place a price
 * belongs; writing it here as well only made the card busier.
 */
function Meta({ parts }) {
  const shown = parts.filter(Boolean);
  if (!shown.length) return null;
  return (
    <span className="text-[0.625cqw] leading-snug tabular-nums" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>
      {shown.map((p, i) => <span key={i}>{i > 0 ? ' · ' : ''}{p}</span>)}
    </span>
  );
}

export default class DraftInviteCard extends Component {
  // Both images are local assets, so they land within a frame or two. The
  // card renders its text immediately and fades the art in behind it: a
  // skeleton would flash for longer than the load it is covering.
  state = { artReady: false };

  render() {
    const {
      meta, mine, senderName, senderAvatarId, recipientName,
      status, coins = 0, keystones = 0, sorceryCards,
      onAccept, onDecline, className,
    } = this.props;

    const sealed = meta?.mode === 'sealed';
    const setKey = meta?.setKey;
    const setLabel = meta?.setLabel || BOOSTER_SETS[setKey]?.label || null;
    const { panel, pack } = setArt(setKey);
    // "Beta Draft" when the event is built on a set, "Ranked invite" when it
    // is not. The old card wrote a whole sentence under this ("invited you
    // to a draft!"), which said nothing the title and the buttons did not.
    const word = modeWord(meta?.mode);
    const title = setLabel ? t('{set} {mode}', { set: setLabel, mode: word }) : t('{mode} invite', { mode: word });
    // A rebroadcast from a host who reopened a paused event. Worth one word,
    // because rejoining an event you are already in is not the same act as
    // accepting a fresh invite (and does not charge entry again).
    const resume = !!meta?.resume;

    const cost = Number(meta?.entryCost || 0);
    const paid = cost > 0;

    // Accept / Decline belong to the recipient of a live invite only. The
    // sender sees what they posted, and a resolved invite states its outcome.
    const actionable = !mine && !!meta?.eventId && !status;

    return (
      <div
        className={cn(
          // The mode tile's plate, minus its hover: this card is not one
          // click, it carries its own buttons, so a plate that lifts under
          // the pointer would promise an action the card itself has not got.
          'vh-mode-tile vh-mode-tile--static vh-mode-tile--ambient replay-card-in',
          className,
        )}
      >
        <div className="vh-mode-tile-art" aria-hidden>
          <div className="absolute inset-0" style={{ background: SET_WASH[setKey] || FALLBACK_WASH }} />
          {panel ? (
            // The wrapper art, blown up and thrown out of focus, is the
            // card's texture. Sharp it would compete with the packshot,
            // which is the same artwork a second time.
            <img
              src={panel}
              alt=""
              className="absolute object-cover"
              style={{
                inset: '-15%',
                width: '130%',
                height: '130%',
                filter: 'blur(0.45cqw) saturate(1.2)',
                opacity: this.state.artReady ? 0.7 : 0,
                transition: 'opacity 320ms ease',
              }}
              onLoad={() => this.setState({ artReady: true })}
              onError={() => this.setState({ artReady: true })}
            />
          ) : null}
          {/* Readability scrim, heaviest under the text and lifting toward
              the pack so the packshot keeps its own light. */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(90deg, rgba(8,6,4,0.86) 0%, rgba(8,6,4,0.66) 45%, rgba(8,6,4,0.2) 78%, rgba(8,6,4,0.4) 100%)' }}
          />
          {pack ? (
            // Overhanging the plate top and bottom, the way the store tiles
            // stand their packs. Squared up and fully inside the frame it
            // read as a thumbnail pasted on rather than as the object the
            // card is built around.
            <img
              src={pack}
              alt=""
              className="absolute object-contain"
              style={{
                ...PACK_BOX,
                transform: `rotate(${PACK_TILT}) scale(${PACK_SCALE[setKey] || 1})`,
                transformOrigin: 'center',
                opacity: this.state.artReady ? 1 : 0,
                transition: 'opacity 320ms ease',
                filter: 'drop-shadow(0 0.5cqw 0.875cqw rgba(0,0,0,0.75))',
              }}
            />
          ) : null}
          {/* Foot scrim, so the action row keeps a ground of its own where
              the pack overhangs the bottom corner. */}
          <div
            className="absolute inset-x-0 bottom-0"
            style={{ height: '45%', background: 'linear-gradient(180deg, transparent 0%, rgba(8,6,4,0.5) 55%, rgba(8,6,4,0.82) 100%)' }}
          />
          <div className="vh-mode-tile-ember" />
          <div className="vh-mode-tile-sheen" />
        </div>

        <div
          className="absolute inset-0 flex flex-col text-left"
          style={{ padding: '0.625cqw 0.75cqw', paddingRight: PACK_GUTTER, zIndex: 4 }}
        >
          <span className="truncate text-[0.875cqw] font-semibold" style={{ color: TEXT_PRIMARY, textShadow: LABEL_SHADOW }}>
            {title}
          </span>

          {mine ? (
            <span className="truncate text-[0.6875cqw]" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>
              {recipientName ? t('Invited {name}', { name: recipientName }) : t('Invite sent')}
            </span>
          ) : (
            <Sender name={senderName || t('A player')} avatarId={senderAvatarId} sorceryCards={sorceryCards} />
          )}

          <div className="mt-0.5">
            <Meta
              parts={[
                resume ? t('Resume') : null,
                sealed ? t('1v1 friend match') : t('{n} players', { n: meta?.podSize || '?' }),
                meta?.scheduledAt && !sealed ? new Date(meta.scheduledAt).toLocaleString() : null,
              ]}
            />
          </div>

          <div className="flex-1 min-h-0" />

          {/* Wraps rather than overflowing. The text column stops at the
              pack's gutter, and a button that slid past it would land on
              the artwork. */}
          <div className="flex flex-wrap items-center gap-1 w-full min-w-0">
            {actionable ? (
              <>
                {paid ? (
                  <AcceptPayControl
                    cost={cost}
                    ksCost={keystoneCostForMode(meta?.mode)}
                    coins={coins}
                    keystones={keystones}
                    onAccept={onAccept}
                  />
                ) : (
                  <button
                    type="button"
                    className="vh-invite-btn is-primary"
                    data-sound={UI.CONFIRM}
                    onClick={() => onAccept?.(null)}
                  >
                    {t('Accept')}
                  </button>
                )}
                <button
                  type="button"
                  className="vh-invite-btn"
                  data-sound={UI.CANCEL}
                  onClick={onDecline}
                >
                  {t('Decline')}
                </button>
              </>
            ) : (
              // Only ever states something it knows. A received invite with
              // no event behind it and no recorded answer says nothing at
              // all rather than guessing at "expired".
              <span className="flex items-center gap-1 text-[0.6875cqw] font-medium" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>
                {!mine && status ? (
                  <span style={{ color: status === 'declined' ? TEXT_MUTED : ACCENT_GOLD }}>&#x2714;</span>
                ) : null}
                {mine ? t('Invite sent') : status === 'declined' ? t('Declined') : status ? t('Accepted') : ''}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
}
